import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Filter, ClipboardCheck, Clock, CheckCircle2, Send, Eye, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { db } from '../../../lib/db';
import { formatDate, getDaysUntil, isOverdue } from '../../../lib/dateUtils';
import { formatStatus, getStatusBadgeColor } from '../../../lib/calculations';
import { useAuthStore } from '../../../store/authStore';

export default function CEOAppraisalsPage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('filter') || 'all');
  const [activeTab, setActiveTab] = useState(searchParams.get('filter') === 'pending' ? 'pending' : 'all');

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [usersData, appraisalsData] = await Promise.all([
        db.users.toArray(),
        db.appraisals.toArray()
      ]);
      
      setUsers(usersData);
      // CEO sees appraisals assigned by them OR sent to them for review
      const ceoAppraisals = appraisalsData.filter(a => 
        a.assignedByCeo === user?.id || 
        a.status === 'sent_to_ceo' ||
        a.ceoId === user?.id
      );
      setAppraisals(ceoAppraisals);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUserById = (id) => users.find(u => u.id === id);

  const pendingCeoReview = appraisals.filter(a => a.status === 'sent_to_ceo');
  const assignedAppraisals = appraisals.filter(a => a.assignedByCeo === user?.id);
  const completedAppraisals = appraisals.filter(a => a.status === 'finalized');

  const filterAppraisals = (list) => {
    return list.filter(a => {
      const employee = getUserById(a.userId);
      const matchesSearch = searchQuery === '' ||
        employee?.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        employee?.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.title?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || a.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  };

  const getAppraisalsForTab = () => {
    switch (activeTab) {
      case 'pending':
        return filterAppraisals(pendingCeoReview);
      case 'assigned':
        return filterAppraisals(assignedAppraisals);
      case 'completed':
        return filterAppraisals(completedAppraisals);
      default:
        return filterAppraisals(appraisals);
    }
  };

  const displayedAppraisals = getAppraisalsForTab();

  if (loading) {
    return <div className="flex items-center justify-center h-64"><span className="text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Appraisals</h1>
          <p className="text-gray-500 mt-1">Review and manage appraisals</p>
        </div>
        <Button onClick={() => navigate('/ceo/templates')} className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <FileText className="w-4 h-4 mr-2" /> Appraisal Template
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="all" className="gap-2">
            All <Badge variant="secondary" className="ml-1">{appraisals.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="pending" className="gap-2">
            Pending Review <Badge variant="secondary" className="ml-1 bg-amber-100 text-amber-700">{pendingCeoReview.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="assigned" className="gap-2">
            Assigned by You <Badge variant="secondary" className="ml-1">{assignedAppraisals.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="completed" className="gap-2">
            Completed <Badge variant="secondary" className="ml-1 bg-emerald-100 text-emerald-700">{completedAppraisals.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mt-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by employee name or title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="sent_to_ceo">Pending CEO Review</SelectItem>
              <SelectItem value="ready_to_evaluate">Ready to Evaluate</SelectItem>
              <SelectItem value="employee_completed">Employee Completed</SelectItem>
              <SelectItem value="manager_review">Manager Reviewing</SelectItem>
              <SelectItem value="finalized">Finalized</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Appraisal List */}
        <div className="mt-6">
          {displayedAppraisals.length > 0 ? (
            <div className="space-y-4">
              {displayedAppraisals.map(appraisal => {
                const employee = getUserById(appraisal.userId);
                const supervisor = getUserById(appraisal.managerId);
                const daysUntil = getDaysUntil(appraisal.dueDate);
                const isAppraisalOverdue = isOverdue(appraisal.dueDate);

                return (
                  <Card 
                    key={appraisal.id} 
                    className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer"
                    onClick={() => navigate(`/ceo/appraisals/${appraisal.id}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center text-white font-medium">
                            {employee?.profile?.firstName?.[0]}{employee?.profile?.lastName?.[0]}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {employee?.profile?.firstName} {employee?.profile?.lastName}
                            </p>
                            <p className="text-sm text-gray-500">{appraisal.title}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <Badge className={getStatusBadgeColor(appraisal.status)}>
                                {formatStatus(appraisal.status)}
                              </Badge>
                              <span className="text-xs text-gray-400">
                                {employee?.profile?.position} • {employee?.profile?.department}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2 mb-2">
                            {supervisor && (
                              <span className="text-xs text-gray-500">
                                Supervisor: {supervisor?.profile?.firstName} {supervisor?.profile?.lastName}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm text-gray-500">Score:</span>
                            <span className="text-sm font-medium text-gray-900">
                              {appraisal.scores?.finalWeightedScore?.toFixed(1) || 'N/A'}
                            </span>
                          </div>
                          <span className={`text-xs ${isAppraisalOverdue ? 'text-rose-600' : daysUntil <= 7 ? 'text-amber-600' : 'text-gray-400'}`}>
                            {appraisal.status === 'finalized' 
                              ? `Finalized ${formatDate(appraisal.finalizedAt)}`
                              : isAppraisalOverdue 
                                ? `Overdue by ${Math.abs(daysUntil)} days`
                                : `Due in ${daysUntil} days`
                            }
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClipboardCheck className="w-10 h-10 text-gray-400" />
              </div>
              <p className="text-lg font-medium text-gray-900">No appraisals found</p>
              <p className="text-gray-500 mt-1">
                {activeTab === 'pending' 
                  ? 'No appraisals are pending your review'
                  : activeTab === 'assigned'
                    ? 'You haven\'t assigned any appraisals yet'
                    : 'Try adjusting your search or filters'
                }
              </p>
              {activeTab === 'assigned' && (
                <Button 
                  className="mt-4 bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => navigate('/ceo/assign')}
                >
                  Assign Your First Appraisal
                </Button>
              )}
            </div>
          )}
        </div>
      </Tabs>
    </div>
  );
}
